'use client';

import { usePostHog } from '@/providers/posthog-context';
import type { Locale } from '@/types/types'; // Import Locale
import { i18nConfig } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { useCurrentLocale } from 'next-i18n-router/client';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch, type FieldErrors, type FieldValues } from 'react-hook-form';

// Custom Hooks & Components
import { buildEmptyFormState } from '@/features/payload-cms/components/form/build-initial-form-state';
import { FormControls } from '@/features/payload-cms/components/form/components/form-controls';
import { FormFieldRenderer, HiddenFieldsContext } from '@/features/payload-cms/components/form/components/form-field-renderer';
import { ProgressBar } from '@/features/payload-cms/components/form/components/progress-bar';
import { SubmissionMessage } from '@/features/payload-cms/components/form/components/submission-message';
import { useFormSteps } from '@/features/payload-cms/components/form/hooks/use-form-steps';
import { useFormSubmission } from '@/features/payload-cms/components/form/hooks/use-form-submission';
import { JobSelectionProvider } from '@/features/payload-cms/components/form/job-selection';
import type { ConditionedBlock, FormBlockType } from '@/features/payload-cms/components/form/types';
import { getEffectivePlacement } from '@/features/payload-cms/components/form/utils/field-placement';
import { getFormStorageKey } from '@/features/payload-cms/components/form/utils/get-form-storage-key';
export type { FormBlockType } from '@/features/payload-cms/components/form/types';
const formatFieldErrors = (errors: FieldErrors): string => {
  return Object.entries(errors).map(([field, error]) => {
    const message = error?.message as string | undefined;
    return typeof message === 'string' && message.length > 0 ? `${field}: ${message}` : field;
  }).join(', ');
};

/**
 * A form placed on a page, or embedded by a feature that knows some answers already.
 *
 * `presetValues` fills answers in and hides their fields, e.g. the Hof on its own dashboard;
 * `initialValues` fills answers in to be changed, e.g. last time's order. `onSubmitted`
 * replaces the confirmation: the embedding feature shows what was sent itself.
 */
export const FormBlock: React.FC<FormBlockType & {
  isPreviewMode?: boolean;
  withBorder?: boolean;
  presetValues?: Record<string, string>;
  initialValues?: Record<string, string>;
  onSubmitted?: () => void;
}> = ({
  form: config,
  isPreviewMode,
  withBorder = true,
  presetValues,
  initialValues,
  onSubmitted
}) => {
  const currentLocale = useCurrentLocale(i18nConfig);
  const locale = (currentLocale ?? 'en') as Locale;
  const posthog = usePostHog();

  // 1. Initialize Form
  const initialFormState = useMemo(() => ({
    ...buildEmptyFormState(config),
    ...initialValues,
    ...presetValues
  }), [config, initialValues, presetValues]);
  const hiddenFields = useMemo(() => new Set(Object.keys(presetValues ?? {})), [presetValues]);
  const formMethods = useForm<FieldValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialFormState
  });

  // Restore form state from sessionStorage on mount. An embedded form with preset answers
  // starts from them: what was left in another context is not this one's.
  useEffect(() => {
    if (presetValues !== undefined) return;
    if (typeof config.id === 'string' && config.id.length > 0) {
      const savedState = sessionStorage.getItem(getFormStorageKey(config.id, 'state'));
      if (typeof savedState === 'string' && savedState.length > 0) {
        try {
          const parsedState = JSON.parse(savedState) as Record<string, unknown>;
          formMethods.reset({
            ...buildEmptyFormState(config),
            ...parsedState
          });
          // Note: We do NOT remove items here immediately,
          // because if the user navigates away and back again (e.g. login failed or multiple redirects), we might want it.
          // It is better to clear it upon successful submission.
        } catch (error) {
          console.error('Failed to parse saved form state:', error);
        }
      }
    }
  }, [config, formMethods, presetValues]);

  // 2. Initialize Hooks
  // Map config.sections (wrappers) to FormSection[]
  const formSections = useMemo(() => config.sections.map(s => s.formSection), [config.sections]);
  const {
    currentStepIndex,
    setCurrentStepIndex,
    steps,
    isFirstStep,
    isLastStep,
    next,
    prev,
    currentActualStep
  } = useFormSteps(formSections, formMethods, config.id, presetValues === undefined);
  const {
    submit,
    status,
    errorMessage,
    previewData,
    reset: resetSubmission
  } = useFormSubmission({
    formId: config.id,
    config,
    isPreviewMode: isPreviewMode ?? false,
    locale,
    setError: formMethods.setError,
    // The steps actually shown, so a server-side field error scrolls to the right one
    // even when a section was skipped by its display condition.
    formSections: steps,
    setCurrentStepIndex,
    onSubmitted
  });
  const handleReset = (): void => {
    resetSubmission();
    formMethods.reset(initialFormState);
    setCurrentStepIndex(0);
  };

  // 3. Render
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const confirmationMessage = config.confirmationMessage;
  const isSplit = currentActualStep?.layout === 'split';

  // Calculate if the main column has any content to display
  const mainPlacementFields = useMemo(() => {
    if (!currentActualStep) return [];
    return currentActualStep.fields.filter(f => {
      if (f.blockType === 'conditionedBlock') {
        const hasMainSub = f.fields.some(sub => getEffectivePlacement(sub) === 'main');
        return f.placement === 'main' || hasMainSub;
      }
      return getEffectivePlacement(f) === 'main';
    });
  }, [currentActualStep]);
  const conditionedMainBlocks = useMemo(() => {
    return mainPlacementFields.filter((f_0): f_0 is ConditionedBlock => f_0.blockType === 'conditionedBlock');
  }, [mainPlacementFields]);
  const {
    control
  } = formMethods;

  // Watch the fields that trigger these blocks
  const triggerValues = useWatch({
    control,
    name: conditionedMainBlocks.map(b => b.displayCondition.field)
  });
  const hasVisibleConditionedMainBlock = useMemo(() => {
    if (conditionedMainBlocks.length === 0) return false;
    return conditionedMainBlocks.some((block, index) => {
      const vals = triggerValues as (string | boolean | number | undefined)[];
      const val = vals[index];
      return val !== undefined && String(val) === block.displayCondition.value;
    });
  }, [conditionedMainBlocks, triggerValues]);
  const hasStaticMainContent = useMemo(() => {
    return mainPlacementFields.some(f_1 => f_1.blockType !== 'conditionedBlock');
  }, [mainPlacementFields]);
  const shouldRenderMain = isSplit && !!(hasStaticMainContent || hasVisibleConditionedMainBlock);

  // Layout-based styles
  const isDualCardLayout = isSplit && shouldRenderMain;
  const onInvalid = (errors: FieldErrors): void => {
    posthog?.capture('form_validation_failed', {
      form_id: config.id,
      error_message: formatFieldErrors(errors),
      source: 'client_final_step'
    });
  };
  const handleNext = async (): Promise<void> => {
    const isValid = await next();
    if (!isValid) {
      posthog?.capture('form_validation_failed', {
        form_id: config.id,
        error_message: formatFieldErrors(formMethods.formState.errors),
        source: 'client_step_transition',
        step: currentStepIndex
      });
    }
  };
  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (isLastStep) {
      void formMethods.handleSubmit(submit, onInvalid)(event);
    } else {
      void handleNext();
    }
  };
  if (!config._localized_status.published) return <></>;
  return <div className="@container w-full">
      <div className={cn('relative w-full', isDualCardLayout ? 'max-w-2xl @[1600px]:max-w-full' : 'max-w-2xl', 'min-h-[100px] bg-transparent p-0 shadow-none')}>
        {/* ... existing error and preview logic ... */}
        {status === 'error' && <div className="mb-4 rounded border border-red-400 bg-red-100 p-4 text-red-700">
            {errorMessage}
          </div>}

        {Boolean(previewData) && <div className="mb-4 rounded border border-gray-400 bg-gray-100 p-4">
            Preview Data: <pre>{JSON.stringify(previewData, undefined, 2)}</pre>
          </div>}

        {/* Success Message / Confirmation */}
        {status === 'success' && config.confirmationType === 'message' ? <SubmissionMessage
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      content={confirmationMessage} onReset={handleReset} locale={locale} /> : <JobSelectionProvider>
            <FormProvider {...formMethods}>
              <HiddenFieldsContext.Provider value={hiddenFields}>
                <form id={config.id} onSubmit={handleSubmit} noValidate>
                  <div className={cn('', isDualCardLayout ? 'grid grid-cols-1 items-start gap-8 @[1600px]:grid-cols-[36rem_1fr] @[1600px]:gap-12' : 'flex flex-col gap-6')}>
                    <aside className={cn('w-full', (isDualCardLayout || withBorder) && 'space-y-4 rounded-xl border border-gray-100 bg-white p-8 shadow-sm')}>
                      {steps.length > 1 && currentActualStep && <ProgressBar locale={locale} currentStepIndex={currentStepIndex} definedSteps={steps} currentActualStep={currentActualStep} />}
                      {currentActualStep && <FormFieldRenderer section={currentActualStep} currentStepIndex={currentStepIndex} formId={config.id} renderMode={isDualCardLayout ? 'sidebar' : 'all'} />}
                      <FormControls locale={locale} isFirst={isFirstStep} isLast={isLastStep} isSubmitting={status === 'loading'}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onNext={handleNext} onPrev={prev} submitLabel={config.submitButtonLabel ?? ''} formId={config.id} />
                    </aside>

                    {isDualCardLayout && <div key="main-column" className="animate-in fade-in fill-mode-backwards flex flex-col rounded-xl border border-gray-100 bg-white p-8 shadow-sm duration-300">
                        <div className={status === 'loading' ? 'pointer-events-none grow opacity-50' : 'grow'}>
                          <FormFieldRenderer section={currentActualStep} currentStepIndex={currentStepIndex} formId={config.id} renderMode="main" />
                        </div>
                        <div className="pt-8 @[1600px]:hidden">
                          <FormControls locale={locale} isFirst={isFirstStep} isLast={isLastStep} isSubmitting={status === 'loading'}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onNext={handleNext} onPrev={prev} submitLabel={config.submitButtonLabel ?? ''} formId={config.id} />
                        </div>
                      </div>}
                  </div>
                </form>
              </HiddenFieldsContext.Provider>
            </FormProvider>
          </JobSelectionProvider>}
      </div>
    </div>;
};